/*
 * card-volume-tooltip.js
 *
 * Populates the product-card "Vanaf … per stuk" info popup
 * (.card-product__from-each-tooltip) with the volume-discount tiers configured
 * in the Bundler app. Bundler does not expose this data in Liquid, but its app
 * embed loads window.bndlr on every page, so on collection/listing pages we
 * read all offers once via window.bndlr.getBundles(), match each visible card's
 * product to its offer, and render the tiers client-side.
 *
 * The info icon is hidden by default (.card-product__from-each-info--empty);
 * we only reveal it for products that have a matching Bundler offer.
 *
 * Offer parsing + tier math live in bundler-volume-core.js (window.BundlerVolume),
 * shared with the cart (cart-volume-pricing.js). The exact shape of getBundles()
 * is not documented; set DEBUG to true here to log the raw payload.
 */
(function () {
  'use strict';

  var DEBUG = false;

  var INFO_SELECTOR = '.card-product__from-each-info';
  var TOOLTIP_SELECTOR = '.card-product__from-each-tooltip';
  var EMPTY_CLASS = 'card-product__from-each-info--empty';

  /* ---------- formatting ---------- */

  function formatPriceNl(cents) {
    return (cents / 100).toFixed(2).replace('.', ',');
  }

  // VAT state: prices are stored net (excl.); the header toggle shows them
  // incl. BTW (net x 1.21). Mirrors assets/vat-switcher.js.
  function vatIncluded() {
    if (window.RotimVat && typeof window.RotimVat.getState === 'function') {
      try { return !!window.RotimVat.getState(); } catch (e) { /* fall through */ }
    }
    try { return localStorage.getItem('rotim:prices-include-vat') === 'true'; } catch (e2) {}
    return false;
  }

  function vatRate() {
    return (window.RotimVat && window.RotimVat.VAT_RATE) || 21;
  }

  // Prefer the theme's shared formatter (quote-cart.js) for consistency.
  function money(cents) {
    if (typeof window.formatMoney === 'function') {
      try {
        return window.formatMoney(cents);
      } catch (e) {
        /* fall through to local formatter */
      }
    }
    return '€' + formatPriceNl(cents);
  }

  // Offer-parsing + tier math live in the shared bundler-volume-core.js module
  // (window.BundlerVolume) so the cart computes identical discounted prices.
  var BV = window.BundlerVolume;

  /* ---------- tier rendering ---------- */

  // Per-unit price (cents) shown for a tier, VAT-aware. When a data02 multiplier
  // is set we divide first then add VAT, mirroring the PDP (syncBndlrPrice:
  // Math.ceil(cents / divisor) before VAT) so card and PDP round identically.
  function tierUnitCents(baseNetCents, tier, mult) {
    var per = BV.computeTier(baseNetCents, tier).perUnitCents;
    if (mult) per = Math.ceil(per / mult);
    if (vatIncluded()) per = Math.round(per * (1 + vatRate() / 100));
    return per;
  }

  function formatQty(qty) {
    return Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace('.', ',');
  }

  function tierLine(baseNetCents, tier, mult) {
    var c = BV.computeTier(baseNetCents, tier);
    var incl = vatIncluded();
    var s = window.volumePricingStrings || {};
    var taxLabel = incl ? (s.taxIncl || 'incl.') : (s.taxExcl || 'excl.');
    var lineTpl = s.tierLine || '[quantity] stuks voor [price] [tax]';
    // data02: tier quantity is multiplied, per-unit price is divided + "/st"
    // (matches the PDP applyDisplayQuantity / syncBndlrPrice behaviour).
    var qty = mult ? tier.minQty * mult : tier.minQty;
    var perUnitSuffix = ((window.volumePricingStrings || {}).perUnitSuffix) || '/st';
    var priceStr = money(tierUnitCents(baseNetCents, tier, mult)) + (mult ? ' ' + perUnitSuffix : '');
    var line = lineTpl
      .replace('[quantity]', formatQty(qty))
      .replace('[price]', priceStr)
      .replace('[tax]', taxLabel);
    if (c.pct != null && c.pct > 0) {
      var discTpl = s.tierDiscount || '([discount]% korting)';
      line += ' ' + discTpl.replace('[discount]', c.pct);
    }
    return line;
  }

  // Tiers worth showing: those with a real saving (skip a 0% minimum-quantity entry).
  function displayedTiers(baseNetCents, tiers) {
    var out = [];
    for (var i = 0; i < tiers.length; i++) {
      var c = BV.computeTier(baseNetCents, tiers[i]);
      if (c.pct == null || c.pct <= 0) continue;
      out.push(tiers[i]);
    }
    return out;
  }

  // Build the tooltip markup (VAT-aware), data02-adjusted when mult is set.
  function buildHtml(baseNetCents, tiers, mult) {
    var disp = displayedTiers(baseNetCents, tiers);
    var html = '';
    for (var i = 0; i < disp.length; i++) {
      html += '<span>' + tierLine(baseNetCents, disp[i], mult) + '</span>';
    }
    return html;
  }

  // Per-unit price for the MIDDLE displayed tier — shown in the always-visible
  // "Vanaf X per stuk" base line. Middle = Math.floor(n/2): for 3 tiers the 2nd,
  // for 2 tiers the upper one. Returns null when there are no savings tiers.
  function baseAmount(baseNetCents, tiers, mult) {
    var disp = displayedTiers(baseNetCents, tiers);
    if (!disp.length) return null;
    var mid = disp[Math.floor(disp.length / 2)];
    return money(tierUnitCents(baseNetCents, mid, mult));
  }

  // data02 cards: replace the main card price (.card-product__unit-price,
  // rendered by card-product.liquid as base / data02) with the middle tier's
  // per-piece price. Both VAT spans are written, so the header toggle keeps
  // working via CSS without a re-render.
  function renderUnitPrice(infoEl, baseNetCents, tiers, mult) {
    var card = infoEl.closest('.card-wrapper');
    var el = card ? card.querySelector('.card-product__unit-price') : null;
    if (!el) return;
    var disp = displayedTiers(baseNetCents, tiers);
    if (!disp.length) return;
    var mid = disp[Math.floor(disp.length / 2)];
    var net = Math.ceil(BV.computeTier(baseNetCents, mid).perUnitCents / mult);
    var incl = Math.round(net * (1 + vatRate() / 100));
    var suffix = ' ' + (((window.volumePricingStrings || {}).perUnitSuffix) || '/st');
    el.innerHTML =
      '<span class="vat-amount vat-amount--excl">' + money(net) + suffix + '</span>' +
      '<span class="vat-amount vat-amount--incl">' + money(incl) + suffix + '</span>';
  }

  // Cards whose tooltips have been populated, so the VAT toggle can re-render
  // them without re-querying Bundler.
  var rendered = [];

  function renderTooltip(infoEl, tiers) {
    var tooltip = infoEl.querySelector(TOOLTIP_SELECTOR);
    if (!tooltip) return;
    var baseNetCents = parseInt(infoEl.getAttribute('data-base-net-cents'), 10);
    if (isNaN(baseNetCents)) return;

    var mult = parseFloat(infoEl.getAttribute('data-unit-multiplier'));
    if (!(mult > 0)) mult = null;

    var html = buildHtml(baseNetCents, tiers, mult);
    if (!html) return; // nothing worth showing — keep the icon hidden
    tooltip.innerHTML = html;
    infoEl.classList.remove(EMPTY_CLASS);

    // Overwrite the visible "Vanaf X per stuk" amount with the middle tier price.
    var wrap = infoEl.parentElement;
    var amountEl = wrap ? wrap.querySelector('.card-product__from-each-amount') : null;
    var amount = baseAmount(baseNetCents, tiers, mult);
    if (amountEl && amount) amountEl.textContent = amount;

    // data02 cards: main price shows the middle tier per-piece price + /st.
    if (mult) renderUnitPrice(infoEl, baseNetCents, tiers, mult);

    rendered.push({
      infoEl: infoEl,
      baseNetCents: baseNetCents,
      tiers: tiers,
      mult: mult,
      amountEl: amountEl
    });
  }

  // Re-render every populated tooltip + base amount for the current VAT state.
  function rerenderForVat() {
    for (var i = 0; i < rendered.length; i++) {
      var r = rendered[i];
      var tooltip = r.infoEl.querySelector(TOOLTIP_SELECTOR);
      if (tooltip) {
        var html = buildHtml(r.baseNetCents, r.tiers, r.mult);
        if (html) tooltip.innerHTML = html;
      }
      if (r.amountEl) {
        var amount = baseAmount(r.baseNetCents, r.tiers, r.mult);
        if (amount) r.amountEl.textContent = amount;
      }
    }
    // If a popup is open, refresh its content too (it was cloned from the
    // in-card holder before this re-render).
    if (activeInfo) showFor(activeInfo);
  }

  /* ---------- floating popup ----------
   *
   * The in-card .card-product__from-each-tooltip is only a data holder. We show
   * a single shared clone appended to <body> so it escapes the overflow:hidden
   * / overflow-x:clip and transformed wrapper of the Swiper sliders the cards
   * live in (z-index can't defeat clipping). It is positioned with
   * position: fixed from the icon's bounding rect, flips above when there is no
   * room below, and is clamped to the viewport so wide popups stay on screen. */

  var VIEWPORT_GUTTER = 8; // px kept between the popup and the viewport edge
  var POPUP_GAP = 7; // px between the icon and the popup (matches the old CSS)
  var floatEl = null;
  var activeInfo = null; // the info element whose popup is currently shown

  function getFloatEl() {
    if (floatEl) return floatEl;
    floatEl = document.createElement('span');
    floatEl.className = TOOLTIP_SELECTOR.slice(1) + ' card-product__from-each-tooltip--floating';
    floatEl.setAttribute('role', 'tooltip');
    document.body.appendChild(floatEl);
    return floatEl;
  }

  function positionFloat(info) {
    var el = getFloatEl();
    var rect = info.getBoundingClientRect();
    var w = el.offsetWidth;
    var h = el.offsetHeight;
    var vw = document.documentElement.clientWidth;
    var vh = document.documentElement.clientHeight;

    // Vertical: prefer below, flip above when it would overflow the viewport.
    var top = rect.bottom + POPUP_GAP;
    if (top + h > vh - VIEWPORT_GUTTER && rect.top - POPUP_GAP - h >= VIEWPORT_GUTTER) {
      top = rect.top - POPUP_GAP - h;
    }

    // Horizontal: anchor to the icon's left, clamp into the viewport.
    var left = rect.left;
    if (left + w > vw - VIEWPORT_GUTTER) left = vw - VIEWPORT_GUTTER - w;
    if (left < VIEWPORT_GUTTER) left = VIEWPORT_GUTTER;

    el.style.top = Math.round(top) + 'px';
    el.style.left = Math.round(left) + 'px';
  }

  function showFor(info) {
    var holder = info.querySelector(TOOLTIP_SELECTOR);
    if (!holder || !holder.innerHTML) return;
    var el = getFloatEl();
    el.innerHTML = holder.innerHTML;
    activeInfo = info;
    // The popup is opacity:0/visibility:hidden until revealed, but still has
    // real offset dimensions, so we can measure and position before showing.
    positionFloat(info);
    el.classList.add('is-visible');
  }

  function hideFloat() {
    activeInfo = null;
    if (floatEl) floatEl.classList.remove('is-visible');
  }

  function bindFloatEvents() {
    // Delegation on document survives the grid DOM swaps that pagination /
    // filtering cause, with no per-card re-binding.
    document.addEventListener('mouseover', function (e) {
      var info = e.target.closest && e.target.closest(INFO_SELECTOR);
      if (info && info !== activeInfo && !info.classList.contains(EMPTY_CLASS)) showFor(info);
    });
    document.addEventListener('mouseout', function (e) {
      var info = e.target.closest && e.target.closest(INFO_SELECTOR);
      if (!info) return;
      // Ignore moves that stay within the same info element.
      if (e.relatedTarget && info.contains(e.relatedTarget)) return;
      if (info === activeInfo) hideFloat();
    });
    document.addEventListener('focusin', function (e) {
      var info = e.target.closest && e.target.closest(INFO_SELECTOR);
      if (info && !info.classList.contains(EMPTY_CLASS)) showFor(info);
    });
    document.addEventListener('focusout', function (e) {
      var info = e.target.closest && e.target.closest(INFO_SELECTOR);
      if (info && info === activeInfo) hideFloat();
    });

    // Keep the popup glued to its icon while open; hide on big layout shifts.
    window.addEventListener('scroll', function () {
      if (activeInfo) positionFloat(activeInfo);
    }, true);
    window.addEventListener('resize', function () {
      if (activeInfo) positionFloat(activeInfo);
    });
  }

  /* ---------- main ---------- */

  function populate() {
    var infos = document.querySelectorAll(INFO_SELECTOR);
    if (!infos.length) return;
    if (!window.bndlr || typeof window.bndlr.getBundles !== 'function') {
      if (DEBUG) console.warn('[card-volume-tooltip] window.bndlr.getBundles not available yet');
      return;
    }

    var bundles;
    try {
      bundles = window.bndlr.getBundles();
    } catch (e) {
      if (DEBUG) console.warn('[card-volume-tooltip] getBundles() threw:', e);
      return;
    }
    if (DEBUG) {
      try { console.log('[card-volume-tooltip] getBundles():', JSON.parse(JSON.stringify(bundles))); } catch (e2) {
        console.log('[card-volume-tooltip] getBundles() (raw):', bundles);
      }
    }

    var offers = BV.getVolumeOffers(bundles);
    if (DEBUG) console.log('[card-volume-tooltip] volume offers matched by getVolumeOffers():', offers.length, offers);
    if (!offers.length) return;

    infos.forEach(function (infoEl) {
      // Already populated for this card.
      if (!infoEl.classList.contains(EMPTY_CLASS)) return;

      var productId = BV.normaliseId(infoEl.getAttribute('data-product-id'));
      if (!productId) return;

      var match = null;
      for (var i = 0; i < offers.length; i++) {
        if (BV.offerAppliesToProduct(offers[i], productId)) { match = offers[i]; break; }
      }
      if (!match) {
        if (DEBUG) console.log('[card-volume-tooltip] no offer matched product', productId);
        return;
      }

      var tiers = BV.extractTiers(match);
      if (DEBUG) console.log('[card-volume-tooltip] product', productId, 'tiers:', tiers);
      if (tiers.length) renderTooltip(infoEl, tiers);
    });
  }

  function init() {
    if (!document.querySelector(TOOLTIP_SELECTOR)) return; // no cards on this page

    // Run once Bundler is ready, and again whenever it signals (re)load.
    document.addEventListener('bundler:loaded', populate);
    document.addEventListener('bundler:rendered', populate);

    // Re-render populated tooltips when the header VAT toggle flips.
    document.addEventListener('rotim:vat-change', rerenderForVat);

    // Show the popup as a body-level clone so it escapes slider clipping.
    bindFloatEvents();

    populate();

    // Bounded fallback while window.bndlr / window.formatMoney finish loading.
    var tries = 0;
    var interval = setInterval(function () {
      tries++;
      populate();
      if (tries > 40 || allPopulated()) clearInterval(interval);
    }, 300);

    // Collection filtering / pagination can swap the grid DOM; re-populate the
    // freshly inserted cards. Debounce so a burst of mutations runs populate
    // once, not per node.
    if ('MutationObserver' in window) {
      var pending = null;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; populate(); }, 200);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function allPopulated() {
    var infos = document.querySelectorAll(INFO_SELECTOR + '.' + EMPTY_CLASS);
    return infos.length === 0;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
